import {
  DEFAULT_SEARCH_PARAMS,
  fromSearchParams,
  toSearchParams,
} from "Config/filterSearchParams";
import _ from "lodash";
import { useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setExceedFilterWarning } from "Redux/Slices/Common";
import useAuth from "./useAuth";

const getTotalAppliedFilters = (_filter) => {
  const filter = _.omit(_filter, ["page", "rowsPerPage"]);
  return Object.keys(filter).reduce((total, key) => {
    let _total = total;
    if (!!filter[key]) {
      if (Array.isArray(filter[key])) {
        _total += filter[key].length;
      } else if (filter[key] !== DEFAULT_SEARCH_PARAMS[key]) {
        _total += 1;
      }
    }
    return _total;
  }, 0);
};

const useFilterAndPagination = (defaultFilter) => {
  const { userTypeConfig } = useAuth({
    entityType: "COMMON",
  });
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const prevPaginationRef = useRef({});

  const { filter, pagination } = useMemo(() => {
    const { page, rowsPerPage, ...filter } = fromSearchParams(
      new URLSearchParams(location.search),
      defaultFilter
    );
    if (!!defaultFilter) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: defaultFilter,
      });
    }
    prevPaginationRef.current = {
      page,
      rowsPerPage,
    };
    return {
      filter,
      pagination: {
        page,
        rowsPerPage,
      },
    };
  }, [location.search]);

  // useEffect(() => {
  //   if (!!defaultFilter) {
  //     navigate(`${location.pathname}${location.search}`, {
  //       replace: true,
  //       state: defaultFilter,
  //     });
  //   }
  // }, [defaultFilter]);

  const updateSearchParams = useCallback(
    (value) => {
      const newFilter = { ...filter, ...value };
      const totalAppliedFilters = getTotalAppliedFilters(newFilter);
      if (
        totalAppliedFilters > (userTypeConfig?.limit?.numFilters || Infinity)
      ) {
        dispatch(setExceedFilterWarning(true));
        return;
      }

      if (
        typeof newFilter.rowsPerPage === "number" &&
        newFilter.rowsPerPage !== prevPaginationRef.current.rowsPerPage
      ) {
        newFilter.page = DEFAULT_SEARCH_PARAMS.page;
      }
      const filterSearchParams = toSearchParams(newFilter, defaultFilter);
      const newLocation = { ...location, search: filterSearchParams, hash: "" };
      if (newLocation.search !== location.search) {
        navigate(newLocation);
      }
    },
    [filter, location]
  );

  const resetFilter = useCallback(() => {
    const filterSearchParams = toSearchParams({ ...pagination });
    const newLocation = { ...location, search: filterSearchParams, hash: "" };
    navigate(newLocation);
  }, [location]);

  const getPageHref = useCallback(
    (page) => {
      const validPage = Math.max(1, page);
      const newFilter = {
        ...filter,
        page: validPage,
        rowsPerPage: pagination.rowsPerPage,
      };
      const filterSearchParams = toSearchParams(newFilter, defaultFilter);
      return `${location.pathname}${filterSearchParams ? `?${filterSearchParams}` : ""}`;
    },
    [filter, pagination.rowsPerPage, defaultFilter, location.pathname]
  );

  return {
    filter,
    pagination,
    updateSearchParams,
    resetFilter,
    getPageHref,
  };
};

export default useFilterAndPagination;
